import { useEffect } from 'react';

interface SeoConfig {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  canonicalPath?: string;
}

function upsertMeta(selector: string, attribute: string, content: string, tagName: string, attrs: Record<string, string>) {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement(tagName) as HTMLMetaElement;
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(attribute, content);
  return el;
}

function getOriginalMeta(selector: string, attribute: string): string {
  const el = document.querySelector(selector) as HTMLMetaElement | null;
  return el?.getAttribute(attribute) || '';
}

export function useTitle({ title, description, ogTitle, ogDescription, canonicalPath }: SeoConfig) {
  const siteUrl = import.meta.env.VITE_SITE_URL;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    // Description
    const prevDesc = getOriginalMeta('meta[name="description"]', 'content');
    upsertMeta('meta[name="description"]', 'content', description, 'meta', { name: 'description' });

    // OG Title
    const resolvedOgTitle = ogTitle || title;
    const prevOgTitle = getOriginalMeta('meta[property="og:title"]', 'content');
    upsertMeta('meta[property="og:title"]', 'content', resolvedOgTitle, 'meta', { property: 'og:title' });

    // OG Description
    const resolvedOgDesc = ogDescription || description;
    const prevOgDesc = getOriginalMeta('meta[property="og:description"]', 'content');
    upsertMeta('meta[property="og:description"]', 'content', resolvedOgDesc, 'meta', { property: 'og:description' });

    // OG URL
    const ogUrl = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;
    const prevOgUrl = getOriginalMeta('meta[property="og:url"]', 'content');
    upsertMeta('meta[property="og:url"]', 'content', ogUrl, 'meta', { property: 'og:url' });

    // Canonical
    const prevCanonical = (document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null)?.getAttribute('href') || '';
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', ogUrl);

    // Twitter Title
    const prevTwitterTitle = getOriginalMeta('meta[name="twitter:title"]', 'content');
    upsertMeta('meta[name="twitter:title"]', 'content', resolvedOgTitle, 'meta', { name: 'twitter:title' });

    // Twitter Description
    const prevTwitterDesc = getOriginalMeta('meta[name="twitter:description"]', 'content');
    upsertMeta('meta[name="twitter:description"]', 'content', resolvedOgDesc, 'meta', { name: 'twitter:description' });

    return () => {
      document.title = previousTitle;

      const descEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (descEl) descEl.content = prevDesc;

      const ogTitleEl = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
      if (ogTitleEl) ogTitleEl.content = prevOgTitle;

      const ogDescEl = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
      if (ogDescEl) ogDescEl.content = prevOgDesc;

      const ogUrlEl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
      if (ogUrlEl) ogUrlEl.content = prevOgUrl;

      if (canonicalLink) canonicalLink.setAttribute('href', prevCanonical);

      const twitterTitleEl = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement | null;
      if (twitterTitleEl) twitterTitleEl.content = prevTwitterTitle;

      const twitterDescEl = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement | null;
      if (twitterDescEl) twitterDescEl.content = prevTwitterDesc;
    };
  }, [title, description, ogTitle, ogDescription, canonicalPath, siteUrl]);
}